/* ============================================
   Módulos de un rol recortado

   Qué pantallas ve una persona con el ERP recortado (hoy: el rol `ventas`).
   Es el gemelo del front de `MODULOS` en `api/src/middleware/auth.ts`: allá
   están los prefijos de API, acá las rutas de pantalla. Son dos listas porque
   son dos cosas distintas —una pantalla pide varias APIs— pero tienen que
   contar la misma historia, así que se tocan juntas.

   El que manda es el del backend. Este sólo decide qué se dibuja: si alguna
   vez discrepan, acá sobra un ítem de menú y allá la petición vuelve 403.
   Nunca al revés, que sería el caso peligroso.
   ============================================ */

/** Ruta → módulo que la habilita. El orden importa: gana la más específica. */
const RUTAS: Array<{ prefijo: string; modulo: string }> = [
  { prefijo: '/prospeccion', modulo: 'crm' },
  { prefijo: '/pipeline', modulo: 'crm' },
  { prefijo: '/leads', modulo: 'crm' },
  { prefijo: '/conversaciones', modulo: 'conversaciones' },
  { prefijo: '/cotizaciones', modulo: 'cotizaciones' },
  { prefijo: '/servicios', modulo: 'servicios' },
  // `/clientes/accesos` y `/clientes/partners` NO están: administran cuentas
  // (altas, bajas, contraseñas), no cartera comercial.
  { prefijo: '/clientes/contactos', modulo: 'clientes' },
  { prefijo: '/clientes', modulo: 'clientes' },
  // La Wiki interna: el manual de ventas vive ahí. Se abre para leer y
  // comentar; reestructurarla sigue siendo cosa de un admin (el candado está
  // en el backend, en `wiki.routes.ts`).
  { prefijo: '/wiki', modulo: 'wiki' },
]

/** Rutas sin módulo: las necesita cualquiera que entre. */
const SIEMPRE = ['/cambiar-password', '/perfil']

/** `null` = sin recorte (admin). Un array, aunque esté vacío, sí recorta. */
export type Modulos = string[] | null | undefined

/** ¿Esta sesión tiene el ERP recortado? */
export function tieneRecorte(modulos: Modulos): modulos is string[] {
  return Array.isArray(modulos)
}

function bajoPrefijo(path: string, prefijo: string): boolean {
  return path === prefijo || path.startsWith(prefijo + '/')
}

/** ¿Puede ver esta ruta? Sin recorte, todo. */
export function puedeVerRuta(modulos: Modulos, path: string): boolean {
  if (!tieneRecorte(modulos)) return true
  if (SIEMPRE.some(p => bajoPrefijo(path, p))) return true
  const entrada = RUTAS.find(r => bajoPrefijo(path, r.prefijo))
  // Una ruta que no está en la tabla es una que nadie recortó a propósito:
  // se niega. Es la misma regla que el backend — lo que se olvide queda
  // prohibido, no abierto.
  return !!entrada && modulos.includes(entrada.modulo)
}

/**
 * Dónde aterriza al entrar. El Dashboard queda fuera de toda cartera: cuenta
 * proyectos y perfiles de equipo —con sus costos por hora— que no son de
 * nadie de ventas. Así que el inicio de un rol recortado es la primera
 * pantalla que sí es suya.
 */
export function rutaDeInicio(modulos: Modulos): string {
  if (!tieneRecorte(modulos)) return '/'
  const orden = ['/pipeline', '/prospeccion', '/conversaciones', '/clientes', '/cotizaciones', '/servicios']
  return orden.find(r => puedeVerRuta(modulos, r)) ?? '/cambiar-password'
}
